// File: @/lib/analytics/core.ts

/**
 * Canonical analytics dispatch function
 *
 * Single entry point for all analytics events in the application.
 *
 * Responsibilities:
 *   1. Production gating — no events fire in dev or Vercel Preview
 *   2. Debug logging in non-production environments
 *   3. Provider routing — dispatches to GA4 and/or Vercel based on event type
 *   4. Isolated error handling per provider — one provider failure won't
 *      prevent the other from receiving the event
 *
 * Routing lives with each provider: an event is sent to GA4 only if it has an
 * entry in GA4_EVENT_NAMES, and to Vercel only if it has one in
 * VERCEL_EVENT_NAMES. There is no second list to keep in sync.
 */

import { ENV } from '@/config/constants';
import { logger } from '@/lib/logger';

import { getAnalyticsDebugCorrelation } from './debug-context';
import { GA4_EVENT_NAMES, sendToGa4 } from './providers/ga4';
import { sendToVercel, VERCEL_EVENT_NAMES } from './providers/vercel';
import type { AnalyticsEvent } from './types';

type ProviderDispatchResult = 'sent' | 'skipped' | 'failed';

// ===================================
// DISPATCH
// ===================================

function isGa4Event(event: AnalyticsEvent): boolean {
  return GA4_EVENT_NAMES[event.name] !== undefined;
}

function isVercelEvent(event: AnalyticsEvent): boolean {
  return VERCEL_EVENT_NAMES[event.name] !== undefined;
}

function getTargetProviders(event: AnalyticsEvent): readonly string[] {
  return [
    ...(isGa4Event(event) ? (['GA4'] as const) : []),
    ...(isVercelEvent(event) ? (['Vercel'] as const) : []),
  ];
}

function shouldLogVerboseDebug(): boolean {
  return ENV.isAnalyticsDebugEnabled && !ENV.isProductionAnalytics;
}

function sendGa4(event: AnalyticsEvent): ProviderDispatchResult {
  if (!isGa4Event(event)) {
    return 'skipped';
  }

  try {
    sendToGa4(event);
    return 'sent';
  } catch (error) {
    logger.error('[Analytics] GA4 delivery failed', error, { event: event.name });
    return 'failed';
  }
}

function sendVercel(event: AnalyticsEvent): ProviderDispatchResult {
  if (!isVercelEvent(event)) {
    return 'skipped';
  }

  try {
    sendToVercel(event);
    return 'sent';
  } catch (error) {
    logger.error('[Analytics] Vercel delivery failed', error, { event: event.name });
    return 'failed';
  }
}

/**
 * Tracks an analytics event through all configured providers.
 *
 * In non-production environments, logs the event to console (via logger)
 * and returns without firing any provider. This ensures:
 *   - Development work never pollutes production analytics data
 *   - Vercel Preview deployments are suppressed
 *   - Local testing produces visible debug output
 *
 * @param event - A typed event created with a creator from events.ts
 *
 * @example
 * import { track } from '@/lib/analytics';
 * import { scrollDepthEvent } from '@/lib/analytics/events';
 *
 * track(scrollDepthEvent(50, 'homepage', 'landing'));
 */
export function track(event: AnalyticsEvent): void {
  const providers = getTargetProviders(event);
  const verboseDebug = shouldLogVerboseDebug();
  const debugCorrelation = verboseDebug ? getAnalyticsDebugCorrelation() : null;

  if (verboseDebug) {
    logger.info('[Analytics Debug] Dispatch start', {
      correlation: debugCorrelation,
      eventName: event.name,
      eventVersion: event.version,
      providers,
      payload: event,
      environment: {
        nodeEnv: process.env['NODE_ENV'],
        vercelEnv: process.env['NEXT_PUBLIC_VERCEL_ENV'] ?? 'undefined',
      },
    });
  }

  if (!ENV.isProductionAnalytics) {
    logger.debug(`📊 [Analytics] ${event.name}`, { event, providers });

    if (verboseDebug) {
      logger.info('[Analytics Debug] Dispatch skipped (non-production analytics environment)', {
        correlation: debugCorrelation,
        eventName: event.name,
        providers,
      });
    }

    return;
  }

  const ga4Result = sendGa4(event);
  const vercelResult = sendVercel(event);

  if (verboseDebug) {
    logger.info('[Analytics Debug] Dispatch complete', {
      correlation: debugCorrelation,
      eventName: event.name,
      providers,
      result: {
        ga4: ga4Result,
        vercel: vercelResult,
      },
    });
  }
}
